// Name Tags: a diamond-shop item that gives one of your creatures a nickname.
//
// A nickname is a look only: it never changes stats, grade, rating or value.
// Tags are bought with diamonds, so they stay on the account (never listed or
// sold). Each naming or renaming uses up one tag; taking a nickname off is free.
// A creature loses its nickname when it changes hands on the market, so a paid
// look never carries tradeable value and a buyer never inherits a name.
import type { CreatureDTO } from './protocol.ts';
import { SPECIES } from './species.ts';
import { BLOCK_ANYWHERE, BLOCK_WORDS } from './nickname-blocklist.ts';

/** The bag item and diamond-shop offer id. */
export const NAME_TAG = 'name-tag';
/** Diamond price of one Name Tag. */
export const NAME_TAG_PRICE = 10;
export const NICKNAME_MAX = 16;
/** The short line shown wherever a nickname is set. */
export const NICKNAME_TRADE_NOTE = 'The nickname comes off if it is sold on the market.';

/** A creature's species name, whatever it is called. */
export function speciesName(c: Pick<CreatureDTO, 'species'>): string {
  return SPECIES[c.species]?.name ?? c.species;
}

/**
 * The name to show for a creature: its nickname, else its species name. Every
 * place that names one of a player's creatures goes through this.
 */
export function displayName(c: Pick<CreatureDTO, 'species'> & { nickname?: string | null }): string {
  return c.nickname || speciesName(c);
}

/** Letters (Latin script, accents allowed), digits, spaces and . , ' ! ? - & : */
const ALLOWED = /^[\p{Script=Latin}0-9 .,'!?\-&:]+$/u;

/** Trim, fold every run of spaces into one, and normalise accents to one form. */
export function tidyNickname(raw: string): string {
  return String(raw ?? '').normalize('NFC').replace(/\s+/g, ' ').trim();
}

const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '@': 'a', '$': 's' };

/** Lower case, accents dropped, number swaps undone; '!' read as an i or as a gap. */
function folds(s: string): string[] {
  const base = s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[013457@$]/g, (ch) => LEET[ch]);
  return [base.replace(/!/g, 'i'), base.replace(/!/g, ' ')];
}

const squeeze = (w: string) => w.replace(/(.)\1+/g, '$1');

/** Whether a (tidied) name hits the blocklist. */
export function isBlocked(name: string): boolean {
  return folds(name).some((f) => {
    const words = f.split(/[^a-z]+/).filter(Boolean);
    // A name spelled out in short pieces ("f u c k", "sh.it") is also read run together;
    // ordinary names are not, so "Mass Hitter" stays fine.
    const spelled = words.length > 1 && words.filter((w) => w.length <= 2).length * 2 >= words.length;
    const forms = new Set([...words, ...(spelled ? [words.join('')] : [])]);
    // Squeezed repeats too: "fuuuck" reads as "fuck".
    for (const w of [...forms]) forms.add(squeeze(w));
    for (const w of forms) if (BLOCK_ANYWHERE.some((b) => w.includes(b))) return true;
    return BLOCK_WORDS.some((b) => forms.has(b));
  });
}

export type NicknameCheck = { ok: true; name: string } | { ok: false; why: string };

/** Server-side rules for a new nickname. The tidied name is what gets saved. */
export function checkNickname(raw: unknown): NicknameCheck {
  if (typeof raw !== 'string') return { ok: false, why: 'Type a name.' };
  const name = tidyNickname(raw);
  if (!name) return { ok: false, why: 'Type a name.' };
  if ([...name].length > NICKNAME_MAX) return { ok: false, why: `Up to ${NICKNAME_MAX} characters.` };
  if (!ALLOWED.test(name)) return { ok: false, why: 'Use letters, numbers, spaces and . , \' ! ? - & : only.' };
  if (!/[\p{L}0-9]/u.test(name)) return { ok: false, why: 'A name needs a letter or a number.' };
  if (isBlocked(name)) return { ok: false, why: 'That name is not allowed. Try another.' };
  return { ok: true, name };
}

/** Whether a search matches a creature by nickname or species name. */
export function nameMatches(c: Pick<CreatureDTO, 'species'> & { nickname?: string | null }, q: string): boolean {
  const s = q.toLowerCase();
  return speciesName(c).toLowerCase().includes(s) || (!!c.nickname && c.nickname.toLowerCase().includes(s));
}
